import { useRef } from 'react';
import { t } from '@transifex/native';
import { Text, Tooltip } from '@chakra-ui/react';
import { history } from 'features/common/history/history';
import { useIsOverflowing } from 'features/common/hooks/useIsOverflowing';
import {
  adminPath,
  userGroupListPath,
} from 'features/admin/components/admin-paths';

export interface GroupDataApp {
  role: string;
  scope: string;
  tenantId: string | null;
}
export interface UserGroupRow {
  id: string;
  name: string;
  description?: string | null;
  roles?: GroupDataApp[];
}

export interface UserGroupRowAction {
  id: string;
  label: string;
  onClick: () => void;
  'data-behavior-analytics-id'?: string;
}

const TruncatedName = ({ name }: { name: string }) => {
  const nameRef = useRef<HTMLParagraphElement | null>(null);
  const isOverflowing = useIsOverflowing(nameRef, 'horizontal', name);
  return (
    <Tooltip label={name} placement='top' isDisabled={!isOverflowing}>
      <Text ref={nameRef} isTruncated maxWidth='220px'>
        {name}
      </Text>
    </Tooltip>
  );
};

const TruncatedDescription = ({
  description,
}: {
  description?: string | null;
}) => {
  const descriptionRef = useRef<HTMLParagraphElement | null>(null);
  const isOverflowing = useIsOverflowing(
    descriptionRef,
    'horizontal',
    description ?? '',
  );
  return (
    <Tooltip label={description} placement='top' isDisabled={!isOverflowing}>
      <Text ref={descriptionRef} isTruncated maxWidth='250px'>
        {description}
      </Text>
    </Tooltip>
  );
};

export const transformDataForTable = (
  data?: UserGroupRow[] | null,
): UserGroupRow[] =>
  data?.map(({ id, name, description, roles }) => {
    return {
      id,
      name: name ?? '',
      description: description ?? '',
      roles: roles ?? [],
    };
  }) ?? [];

export const getTableOptions = () => {
  return {
    rowActions: (groupListRow: {
      original: UserGroupRow;
    }): UserGroupRowAction[] => {
      const { id } = groupListRow.original;
      return [
        {
          id: 'view-details',
          label: t('View Details'),
          onClick: () => {
            history.push(`${adminPath}${userGroupListPath}/${id}/members`);
          },
          'data-behavior-analytics-id': 'view-details - submenu',
        },
      ];
    },
    columns: [
      {
        Header: t('Group name'),
        accessor: (row: UserGroupRow) => <TruncatedName name={row.name} />,
        disableSortBy: true,
      },
      {
        Header: t('Description'),
        accessor: ({ description }: UserGroupRow) => (
          <TruncatedDescription description={description} />
        ),
        disableSortBy: true,
      },
    ],
  };
};
